import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Inbox, Send, ShieldOff } from 'lucide-react';
import LeadList from './leads/LeadList';
import LeadThread from './leads/LeadThread';
import NewLeadModal from './leads/NewLeadModal';
import NeedsAssignmentPanel from './leads/NeedsAssignmentPanel';
import LeadRequestModal from './leads/LeadRequestModal';
import LeadRequestsPanel from './leads/LeadRequestsPanel';
import VoucherPerformanceStat from './leads/VoucherPerformanceStat';
import { useAdminAuth } from '../../context/AdminAuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const LeadsInbox = ({ onUnreadChange, role = 'admin' }) => {
  const { admin } = useAdminAuth();
  // AdminAuthContext polls check-auth every 20s / on focus, so this changes
  // whenever Admin edits this user's own region access — everything below
  // that depends on region scope must re-fetch when it does, or the Leads
  // inbox stays stuck on regions granted/revoked before the last full reload.
  const sessionRegionsKey = JSON.stringify(admin?.regions || []);
  const [leads, setLeads] = useState([]);
  const [loadingLeads, setLoadingLeads] = useState(true);
  const [filter, setFilter] = useState('all');
  const [regions, setRegions] = useState([]);
  const [regionFilter, setRegionFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedLeadId, setSelectedLeadId] = useState(null);
  const [thread, setThread] = useState(null);
  const [loadingThread, setLoadingThread] = useState(false);
  const [sending, setSending] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [teamMembers, setTeamMembers] = useState([]);
  const [notes, setNotes] = useState([]);
  const [activity, setActivity] = useState([]);
  const [showNewLead, setShowNewLead] = useState(false);
  const [unmatched, setUnmatched] = useState([]);
  const [showUnmatched, setShowUnmatched] = useState(false);
  const [leadRequests, setLeadRequests] = useState([]);
  const [showLeadRequestModal, setShowLeadRequestModal] = useState(false);
  const [showLeadRequests, setShowLeadRequests] = useState(false);
  const seenPendingRequestIds = useRef(null);
  const [suppressedToday, setSuppressedToday] = useState(null);
  const [testMode, setTestMode] = useState(null);
  const canManage = role === 'admin' || role === 'manager';
  // Agents are always forced into Safe Test Mode regardless of the Admin toggle.
  const testModeActive = role === 'agent' ? true : !!testMode?.effective;

  useEffect(() => {
    if (!canManage) return;
    const fetchSuppressed = () => {
      axios.get(`${BACKEND_URL}/api/admin/leads/suppressed-count`, { withCredentials: true })
        .then((res) => setSuppressedToday(res.data.total))
        .catch(() => {});
    };
    fetchSuppressed();
    const interval = setInterval(fetchSuppressed, 60000);
    return () => clearInterval(interval);
  }, [canManage]);

  useEffect(() => {
    const fetchTestMode = () => {
      axios.get(`${BACKEND_URL}/api/admin/email-identities/test-mode`, { withCredentials: true })
        .then((res) => setTestMode(res.data))
        .catch(() => {});
    };
    fetchTestMode();
    const interval = setInterval(fetchTestMode, 60000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!canManage) return;
    axios.get(`${BACKEND_URL}/api/admin/team-accounts`, { withCredentials: true })
      .then((res) => setTeamMembers((res.data.members || []).filter((m) => m.role === 'agent')))
      .catch(() => {});
  }, [canManage, sessionRegionsKey]);

  const fetchLeads = useCallback(async () => {
    setLoadingLeads(true);
    try {
      const params = {};
      if (filter === 'unread') params.unread = true;
      if (regionFilter !== 'all') params.region = regionFilter;
      if (statusFilter !== 'all') params.status = statusFilter;
      const res = await axios.get(`${BACKEND_URL}/api/admin/leads`, { params, withCredentials: true });
      setLeads(res.data.leads || []);
    } catch (err) {
      toast.error('Failed to load leads');
    } finally {
      setLoadingLeads(false);
    }
  }, [filter, regionFilter, statusFilter, sessionRegionsKey]);

  useEffect(() => { fetchLeads(); }, [fetchLeads]);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/admin/leads/regions`, { withCredentials: true })
      .then((res) => setRegions(res.data.regions || []))
      .catch(() => {});
  }, [sessionRegionsKey]);

  const fetchUnmatched = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/leads/unmatched`, { withCredentials: true });
      setUnmatched(res.data.messages || []);
    } catch (err) {
      // Silent — badge just stays at its last known value
    }
  }, []);

  useEffect(() => {
    fetchUnmatched();
    const interval = setInterval(fetchUnmatched, 30000);
    return () => clearInterval(interval);
  }, [fetchUnmatched]);

  const openLead = async (leadId) => {
    setSelectedLeadId(leadId);
    setLoadingThread(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/leads/${leadId}/thread`, { withCredentials: true });
      setThread(res.data);
      // Reflect "read" state immediately in the list + refresh the header badge
      setLeads((prev) => prev.map((l) => (l.lead_id === leadId ? { ...l, unread: false } : l)));
      onUnreadChange?.();
    } catch (err) {
      toast.error('Failed to load conversation');
    } finally {
      setLoadingThread(false);
    }
    axios.get(`${BACKEND_URL}/api/admin/leads/${leadId}/notes`, { withCredentials: true })
      .then((res) => setNotes(res.data.notes || [])).catch(() => setNotes([]));
    axios.get(`${BACKEND_URL}/api/admin/leads/${leadId}/activity`, { withCredentials: true })
      .then((res) => setActivity(res.data.events || [])).catch(() => setActivity([]));
  };

  const sendReply = async (body, fromEmail, files) => {
    if (!selectedLeadId) return false;
    setSending(true);
    try {
      const formData = new FormData();
      formData.append('body', body);
      formData.append('from_email', fromEmail);
      files.forEach((f) => formData.append('attachments', f));
      const res = await axios.post(
        `${BACKEND_URL}/api/admin/leads/${selectedLeadId}/reply`,
        formData,
        { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } }
      );
      setThread((prev) => prev ? { ...prev, messages: [...prev.messages, res.data] } : prev);
      setLeads((prev) => prev.map((l) => (
        l.lead_id === selectedLeadId
          ? { ...l, last_message_preview: body.slice(0, 140), last_message_at: res.data.created_at }
          : l
      )));
      if (res.data.status === 'failed') {
        toast.error('Message saved, but the email failed to send. You can retry it from the thread.');
      } else if (res.data.status === 'suppressed') {
        toast('Reply saved, but suppressed by Safe Test Mode (not actually sent).');
      } else {
        toast.success('Reply sent!');
      }
      return true;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send reply');
      return false;
    } finally {
      setSending(false);
    }
  };

  const retryMessage = async (messageId, force = false) => {
    if (!selectedLeadId) return;
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/admin/leads/${selectedLeadId}/messages/${messageId}/retry`,
        {},
        { params: force ? { force: true } : {}, withCredentials: true }
      );
      setThread((prev) => prev
        ? { ...prev, messages: prev.messages.map((m) => (m.id === messageId ? res.data : m)) }
        : prev);
      if (res.data.status === 'failed') {
        toast.error('Still couldn\'t send — please try again in a moment.');
      } else if (res.data.status === 'suppressed') {
        toast('Still suppressed by Safe Test Mode.');
      } else {
        toast.success(force ? 'Sent for real!' : 'Message re-sent!');
      }
    } catch (err) {
      setThread((prev) => prev
        ? { ...prev, messages: prev.messages.map((m) => (m.id === messageId ? { ...m, status: 'failed' } : m)) }
        : prev);
      toast.error(err.response?.data?.detail || 'Retry failed');
    }
  };

  const refreshDetails = (leadId) => {
    axios.get(`${BACKEND_URL}/api/admin/leads/${leadId}/notes`, { withCredentials: true })
      .then((res) => setNotes(res.data.notes || [])).catch(() => {});
    axios.get(`${BACKEND_URL}/api/admin/leads/${leadId}/activity`, { withCredentials: true })
      .then((res) => setActivity(res.data.events || [])).catch(() => {});
  };

  const updateStatus = async (status) => {
    if (!selectedLeadId) return;
    try {
      await axios.put(
        `${BACKEND_URL}/api/admin/leads/${selectedLeadId}/status`,
        {},
        { params: { status }, withCredentials: true }
      );
      setThread((prev) => prev ? { ...prev, conversation: { ...prev.conversation, status } } : prev);
      setLeads((prev) => prev.map((l) => (l.lead_id === selectedLeadId ? { ...l, status } : l)));
      refreshDetails(selectedLeadId);
      toast.success('Status updated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update status');
    }
  };

  const deleteLead = async () => {
    if (!selectedLeadId) return;
    setDeleting(true);
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/leads/${selectedLeadId}`, { withCredentials: true });
      setLeads((prev) => prev.filter((l) => l.lead_id !== selectedLeadId));
      setSelectedLeadId(null);
      setThread(null);
      onUnreadChange?.();
      toast.success('Lead deleted');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete lead');
    } finally {
      setDeleting(false);
    }
  };

  const assignLead = async (assignedManagerId) => {
    if (!selectedLeadId) return;
    try {
      await axios.put(`${BACKEND_URL}/api/admin/leads/${selectedLeadId}/assign`,
        {}, { params: { assigned_manager_id: assignedManagerId }, withCredentials: true });
      setThread((prev) => prev ? { ...prev, conversation: { ...prev.conversation, assigned_manager_id: assignedManagerId || null } } : prev);
      setLeads((prev) => prev.map((l) => (l.lead_id === selectedLeadId ? { ...l, assigned_manager_id: assignedManagerId || null } : l)));
      refreshDetails(selectedLeadId);
      toast.success(assignedManagerId ? 'Lead assigned' : 'Lead unassigned');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to assign lead');
    }
  };

  const addNote = async (body) => {
    if (!selectedLeadId) return;
    try {
      const res = await axios.post(`${BACKEND_URL}/api/admin/leads/${selectedLeadId}/notes`, { body }, { withCredentials: true });
      setNotes((prev) => [...prev, res.data]);
      toast.success('Note added');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add note');
    }
  };

  const createLead = async (payload) => {
    try {
      const res = await axios.post(`${BACKEND_URL}/api/admin/leads`, payload, { withCredentials: true });
      if (res.data.message?.status === 'failed') {
        toast.error('Lead created, but the first email failed to send. You can retry it from the thread.');
      } else if (res.data.message?.status === 'suppressed') {
        toast('Lead created, but suppressed by Safe Test Mode (not actually sent).');
      } else {
        toast.success('Lead created and message sent');
      }
      await fetchLeads();
      openLead(res.data.lead_id);
      return true;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create lead');
      return false;
    }
  };

  const resolveUnmatched = async (unmatchedId, leadId) => {
    if (!leadId) return;
    try {
      await axios.post(`${BACKEND_URL}/api/admin/leads/unmatched/${unmatchedId}/resolve`, { lead_id: leadId }, { withCredentials: true });
      toast.success('Message attached to lead');
      fetchUnmatched();
      fetchLeads();
      if (selectedLeadId === leadId) openLead(leadId);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to attach message');
    }
  };

  const suggestUnmatched = async (unmatchedId, leadId, note) => {
    if (!leadId) return;
    try {
      await axios.post(`${BACKEND_URL}/api/admin/leads/unmatched/${unmatchedId}/suggest`, { lead_id: leadId, note }, { withCredentials: true });
      toast.success('Suggestion added');
      fetchUnmatched();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add suggestion');
    }
  };

  const fetchLeadRequests = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/lead-requests`, { withCredentials: true });
      const list = res.data.requests || [];
      const pending = list.filter((r) => r.status === 'pending');
      if (canManage) {
        const pendingIds = new Set(pending.map((r) => r.id));
        if (seenPendingRequestIds.current !== null) {
          const newOnes = pending.filter((r) => !seenPendingRequestIds.current.has(r.id));
          newOnes.forEach((r) => toast(`New lead request from ${r.requested_by}: ${r.target_name}`));
        }
        seenPendingRequestIds.current = pendingIds;
      }
      setLeadRequests(list);
    } catch (err) {
      // Silent — badge just stays at its last known value
    }
  }, [canManage]);

  useEffect(() => {
    fetchLeadRequests();
    const interval = setInterval(fetchLeadRequests, 30000);
    return () => clearInterval(interval);
  }, [fetchLeadRequests]);

  const createLeadRequest = async (payload) => {
    try {
      await axios.post(`${BACKEND_URL}/api/admin/lead-requests`, payload, { withCredentials: true });
      toast.success('Lead request submitted');
      fetchLeadRequests();
      return true;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit request');
      return false;
    }
  };

  const approveLeadRequest = async (requestId) => {
    try {
      await axios.put(`${BACKEND_URL}/api/admin/lead-requests/${requestId}/approve`, {}, { withCredentials: true });
      toast.success('Request approved — lead created');
      fetchLeadRequests();
      fetchLeads();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to approve request');
    }
  };

  const rejectLeadRequest = async (requestId, reason) => {
    try {
      await axios.put(`${BACKEND_URL}/api/admin/lead-requests/${requestId}/reject`, { reason }, { withCredentials: true });
      toast.success('Request rejected');
      fetchLeadRequests();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reject request');
    }
  };

  return (
    <div className="space-y-4" data-testid="leads-inbox">
      {testModeActive && (
        <div
          className="flex items-center gap-2.5 bg-purple-50 border-2 border-purple-200 text-purple-800 rounded-lg px-4 py-3 text-sm font-semibold"
          data-testid="safe-test-mode-banner"
        >
          <ShieldOff className="w-5 h-5 flex-shrink-0" />
          <span>
            Test Mode is ON — outbound emails to unverified/test addresses are being suppressed, not actually sent.
            {role === 'agent' && ' Agents always send in Test Mode.'}
          </span>
          {canManage && suppressedToday !== null && (
            <span className="ml-auto text-xs text-purple-600 font-medium whitespace-nowrap" data-testid="safe-test-mode-banner-count">
              {suppressedToday} suppressed today
            </span>
          )}
        </div>
      )}
      <div className="flex items-center gap-2 flex-wrap">
        {canManage && (
          <button
            type="button"
            onClick={() => setShowNewLead(true)}
            data-testid="new-lead-button"
            className="inline-flex items-center gap-1.5 bg-slate-900 text-white px-3 py-2 rounded-lg text-sm font-medium hover:bg-slate-800 transition-colors"
          >
            <Plus className="w-4 h-4" /> New Lead
          </button>
        )}
        {role === 'agent' && (
          <button
            type="button"
            onClick={() => setShowLeadRequestModal(true)}
            data-testid="request-lead-button"
            className="inline-flex items-center gap-1.5 bg-slate-900 text-white px-3 py-2 rounded-lg text-sm font-medium hover:bg-slate-800 transition-colors"
          >
            <Plus className="w-4 h-4" /> Request Lead
          </button>
        )}
        <button
          type="button"
          onClick={() => setShowLeadRequests(true)}
          data-testid="lead-requests-button"
          className="inline-flex items-center gap-1.5 bg-indigo-50 text-indigo-700 border border-indigo-200 px-3 py-2 rounded-lg text-sm font-medium hover:bg-indigo-100 transition-colors"
        >
          <Send className="w-4 h-4" /> {canManage ? 'Lead Requests' : 'My Requests'}
          {leadRequests.filter((r) => r.status === 'pending').length > 0 && (
            <span className="bg-indigo-500 text-white text-[11px] font-bold px-1.5 py-0.5 rounded-full" data-testid="lead-requests-count">
              {leadRequests.filter((r) => r.status === 'pending').length}
            </span>
          )}
        </button>
        <button
          type="button"
          onClick={() => setShowUnmatched(true)}
          data-testid="needs-assignment-button"
          className="inline-flex items-center gap-1.5 bg-amber-50 text-amber-700 border border-amber-200 px-3 py-2 rounded-lg text-sm font-medium hover:bg-amber-100 transition-colors"
        >
          <Inbox className="w-4 h-4" /> Needs Assignment
          {unmatched.length > 0 && (
            <span className="bg-amber-500 text-white text-[11px] font-bold px-1.5 py-0.5 rounded-full" data-testid="needs-assignment-count">
              {unmatched.length}
            </span>
          )}
        </button>
        {canManage && suppressedToday !== null && suppressedToday > 0 && (
          <span
            className="inline-flex items-center gap-1.5 bg-purple-50 text-purple-700 border border-purple-200 px-3 py-2 rounded-lg text-sm font-medium"
            data-testid="suppressed-today-counter"
            title="Outbound sends Safe Test Mode has blocked in the last 24h"
          >
            <ShieldOff className="w-4 h-4" /> {suppressedToday} suppressed today
          </span>
        )}
        {role !== 'admin' && <VoucherPerformanceStat />}
      </div>
      <div className="flex flex-col md:flex-row gap-4">
        <LeadList
          leads={leads}
          selectedLeadId={selectedLeadId}
          onSelect={openLead}
          filter={filter}
          setFilter={setFilter}
          loading={loadingLeads}
          regions={regions}
          regionFilter={regionFilter}
          setRegionFilter={setRegionFilter}
          statusFilter={statusFilter}
          setStatusFilter={setStatusFilter}
        />
        <LeadThread
          thread={thread}
          loading={loadingThread}
          onSendReply={sendReply}
          onRetry={retryMessage}
          sending={sending}
          onStatusChange={updateStatus}
          onDelete={deleteLead}
          deleting={deleting}
          role={role}
          canManage={canManage}
          teamMembers={teamMembers}
          onAssign={assignLead}
          notes={notes}
          activity={activity}
          onAddNote={addNote}
        />
      </div>
      {showNewLead && <NewLeadModal onClose={() => setShowNewLead(false)} onCreate={createLead} />}
      {showLeadRequestModal && <LeadRequestModal onClose={() => setShowLeadRequestModal(false)} onCreate={createLeadRequest} />}
      {showLeadRequests && (
        <LeadRequestsPanel
          requests={leadRequests}
          canManage={canManage}
          regions={regions}
          onApprove={approveLeadRequest}
          onReject={rejectLeadRequest}
          onClose={() => setShowLeadRequests(false)}
        />
      )}
      {showUnmatched && (
        <NeedsAssignmentPanel
          messages={unmatched}
          leads={leads}
          role={role}
          onResolve={resolveUnmatched}
          onSuggest={suggestUnmatched}
          onClose={() => setShowUnmatched(false)}
        />
      )}
    </div>
  );
};

export default LeadsInbox;
